import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, idr, pesanGalat } from "../../lib/api";
import { toast } from "sonner";
import { BookOpen, Wallet, TrendingDown, TrendingUp, Scale, RotateCcw, X, ExternalLink, Upload, Zap } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { UploadModal } from "./FinanceRekonsiliasi";

// Label tampilan per kategori akun — sama dengan yang dipakai FinanceCOA.jsx,
// tapi disalin ringkas di sini (bukan diimpor) karena hanya perlu label untuk
// <optgroup>, bukan warna/ikon.
const CATEGORY_LABEL = {
  kas: "Kas Toko",
  bank: "Bank",
  petty_cash: "Petty Cash",
  piutang: "Piutang Usaha",
  dibayar_dimuka: "Beban Dibayar di Muka",
  persediaan: "Persediaan",
  aset_tetap: "Aset Tetap",
  utang: "Utang Usaha",
  modal: "Modal & Ekuitas",
  pendapatan: "Pendapatan",
  hpp: "Harga Pokok",
  beban: "Beban Operasional",
};

const firstDayOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const LIMIT = 100;

// Baris jurnal punya SALAH SATU dari 3 field sumber (buku_besar.py), tidak
// pernah lebih dari satu — resolver ini menerjemahkannya ke {type, id} untuk
// dipakai SourceDetailModal. Baris tanpa sumber (mis. jurnal manual /
// penyesuaian) balik `null` — kolom Referensi tetap teks biasa, BUKAN tautan
// mati (arahan tugas: jangan tampilkan sebagai tautan kalau tidak ada yang
// bisa dibuka).
function _sourceOf(it) {
  if (it.source_sale_id) return { type: "sale", id: it.source_sale_id };
  if (it.source_acq_id) return { type: "acq", id: it.source_acq_id };
  if (it.source_payroll_id) return { type: "payroll", id: it.source_payroll_id };
  return null;
}

export default function FinanceBukuBesar() {
  const [akunList, setAkunList] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const { can } = useAuth();
  const navigate = useNavigate();
  const [header, setHeader] = useState(null);
  // Rekonsiliasi per baris (brief owner 29 Agu). Mesin unggah CSV & pencocokan
  // otomatisnya SENGAJA yang sudah ada di modul Rekonsiliasi Bank — dua mesin
  // pencocokan yang berpisah jalan adalah cacat yang sudah berkali-kali
  // menggigit kodebase ini.
  const [bisaRekon, setBisaRekon] = useState(false);
  const [rekonBusy, setRekonBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [draft, setDraft] = useState({});   // { [ref]: {nominal_masuk, keterangan_selisih, sla_tanggal} }
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalDebitPeriode, setTotalDebitPeriode] = useState(0);
  const [totalKreditPeriode, setTotalKreditPeriode] = useState(0);
  const [saldoAkhir, setSaldoAkhir] = useState(0);
  const [terpotong, setTerpotong] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Drill-down: baris jurnal yang punya sumber dokumen (penjualan/akuisisi/slip
  // gaji) bisa diklik untuk membuka detailnya di modal — lihat `_sourceOf` &
  // `SourceDetailModal` di bawah. `null` = tidak ada modal terbuka.
  const [sourceModal, setSourceModal] = useState(null);
  // F1 (brief owner 12 Sep 2026): "ketika pengguna menekan bagian Deskripsi,
  // sistem akan secara otomatis mengarahkan ke input transaksi awal yang
  // menjadi sumber pencatatan tersebut".
  //
  // Alamat tujuannya TIDAK ditebak di layar. `GET /finance/jurnal/{txn}` sudah
  // punya tabel resolusi sumber (`buku_besar.py::_SPEK_SUMBER`, 27 spek) yang
  // dipakai bersama halaman detail dan pemindai jurnal-tanpa-sumber — menyalin
  // pemetaannya ke sini akan melahirkan versi kedua yang berpisah jalan begitu
  // ada modul baru.
  const [telusurBusy, setTelusurBusy] = useState(null);   // transaction_id

  useEffect(() => {
    api.get("/finance/buku-besar/akun")
      .then(r => setAkunList(r.data || []))
      .catch(() => toast.error("Gagal memuat daftar akun"));
  }, []);

  const akunGrouped = useMemo(() => {
    const map = {};
    for (const a of akunList) {
      const key = a.category || "lainnya";
      if (!map[key]) map[key] = [];
      map[key].push(a);
    }
    return map;
  }, [akunList]);

  // Nomor urut request (pola KartuStok.jsx): ganti akun/rentang tanggal
  // beruntun, atau klik "muat lagi" tepat sebelum filter berganti, tidak
  // boleh membuat respons yang datang belakangan menimpa state filter yang
  // sedang aktif sekarang.
  const reqSeq = useRef(0);
  const fetchPage = async (offset, { append }) => {
    if (!accountId) return;
    const seq = ++reqSeq.current;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const r = await api.get("/finance/buku-besar", {
        params: {
          account_id: accountId,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          limit: LIMIT,
          offset,
        },
      });
      if (seq !== reqSeq.current) return;
      setHeader(r.data.header);
      setBisaRekon(!!r.data.bisa_rekonsiliasi);
      setTotalRows(r.data.total_rows || 0);
      setTotalDebitPeriode(r.data.total_debit_periode || 0);
      setTotalKreditPeriode(r.data.total_kredit_periode || 0);
      setSaldoAkhir(r.data.saldo_akhir || 0);
      setTerpotong(!!r.data.terpotong);
      setItems(prev => append ? [...prev, ...(r.data.items || [])] : (r.data.items || []));
    } catch (e) {
      if (seq !== reqSeq.current) return;
      toast.error(e?.response?.data?.detail || "Gagal memuat buku besar");
      if (!append) { setHeader(null); setItems([]); setTotalRows(0); }
    } finally {
      if (seq === reqSeq.current) { setLoading(false); setLoadingMore(false); }
    }
  };

  useEffect(() => {
    if (!accountId) { setHeader(null); setItems([]); setTotalRows(0); setTerpotong(false); return; }
    fetchPage(0, { append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, dateFrom, dateTo]);

  /** F1 — buka transaksi ASAL dari sebuah baris buku besar.
   *
   *  Tiga kemungkinan, ketiganya harus jelas bagi pengguna:
   *  1. sumbernya dikenali DAN pengguna berhak → langsung ke halaman modulnya;
   *  2. sumbernya dikenali tapi pengguna TIDAK berhak → jangan lempar ke
   *     halaman yang akan menolaknya; jelaskan izin apa yang kurang;
   *  3. sumbernya tidak dikenali (jurnal manual/penyesuaian) → jatuh ke
   *     halaman Detail Jurnal, yang selalu bisa menampilkan seluruh sisi
   *     transaksinya. Itu tetap "menelusuri ke pencatatan awal", cuma
   *     sejauh yang benar-benar diketahui sistem.
   *
   *  Keputusan yang dicatat di BRIEF-12-SEP-2026.md: untuk modul yang
   *  halamannya belum bisa MENYOROT baris tertentu, tautannya mendarat di
   *  halaman daftar modul itu — bukan di barisnya. Menyorot baris di sembilan
   *  halaman kira-kira dua kali lipat pekerjaannya, dan versi ini sudah
   *  menjawab "telusuri ke sumbernya".
   */
  const telusuriSumber = async (it) => {
    const txn = (it.ref || "").trim();
    if (!txn) { toast.error("Baris ini tidak punya nomor transaksi"); return; }
    setTelusurBusy(txn);
    try {
      const r = await api.get(`/finance/jurnal/${encodeURIComponent(txn)}`);
      const s = r.data?.sumber || {};
      if (s.ditemukan && s.route) {
        if (s.izin_modul && !can(s.izin_modul, "view")) {
          toast.error(`Sumbernya ${s.label || "modul lain"}, tapi Anda belum punya izin ` +
                      `melihatnya (${s.izin_modul}). Mintakan izinnya ke admin.`);
          return;
        }
        navigate(s.route);
        return;
      }
      // Sumber tidak dikenali — halaman Detail Jurnal tetap menampilkan
      // seluruh sisi transaksinya, jadi penelusurannya tidak buntu.
      navigate(`/finance/jurnal/${encodeURIComponent(txn)}`);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menelusuri transaksi asalnya"));
    } finally {
      setTelusurBusy(null);
    }
  };

  const simpanRekon = async (it, tambahan) => {
    setRekonBusy(true);
    try {
      const d = draft[it.ref] || {};
      const body = {
        account_id: accountId, transaction_id: it.ref,
        nominal_masuk: d.nominal_masuk !== undefined && d.nominal_masuk !== ""
          ? Number(d.nominal_masuk) : undefined,
        keterangan_selisih: d.keterangan_selisih,
        sla_tanggal: d.sla_tanggal,
        ...tambahan,
      };
      Object.keys(body).forEach(k => body[k] === undefined && delete body[k]);
      await api.put("/finance/buku-besar/rekon", body);
      toast.success("Rekonsiliasi baris tersimpan");
      setDraft(p => { const n = { ...p }; delete n[it.ref]; return n; });
      fetchPage(0, { append: false });
    } catch (e) {
      // Syarat centang ditegakkan di BACKEND; pesannya ditampilkan apa adanya
      // supaya pengguna tahu APA yang kurang, bukan cuma "gagal menyimpan".
      toast.error(e?.response?.data?.detail || "Gagal menyimpan rekonsiliasi");
    } finally { setRekonBusy(false); }
  };

  const autoMatch = async () => {
    setRekonBusy(true);
    try {
      const r = await api.post(`/finance/rekonsiliasi/auto-match?akun_id=${accountId}`);
      toast.success(r.data?.message || "Auto-match selesai");
      fetchPage(0, { append: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-match gagal");
    } finally { setRekonBusy(false); }
  };

  const muatLagi = () => fetchPage(items.length, { append: true });

  return (
    <div className="p-4 md:p-8 max-w-[1400px] space-y-6" data-testid="finance-buku-besar">
      <header>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Laporan &amp; Kontrol</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-emerald-600" /> Buku Besar
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Kartu satu akun kronologis dengan saldo berjalan — pilih akun untuk melihat seluruh mutasinya.
        </p>
      </header>

      {/* `UploadModal` memanggil `onClose()` TANPA argumen (lihat
          FinanceRekonsiliasi.jsx:418 & :435), jadi memuat ulang secara
          bersyarat berarti tabel tidak pernah menyegar sesudah unggah.
          Dimuat ulang tanpa syarat — menutup modal tanpa mengunggah apa pun
          paling banter memuat ulang sekali dengan sia-sia. */}
      {showUpload && (
        <UploadModal akunId={accountId} periode={(dateFrom || "").slice(0, 7)}
          onClose={() => { setShowUpload(false); fetchPage(0, { append: false }); }} />
      )}

      {/* Unggah mutasi rekening & cocokkan otomatis — fungsinya SAMA dengan
          menu Rekonsiliasi Bank (brief owner 29 Agu), dan memang memanggil
          endpoint yang sama. Hanya muncul untuk akun yang punya mutasi
          rekening; untuk akun beban/pendapatan tidak ada yang bisa dicocokkan. */}
      {accountId && bisaRekon && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="bb-rekon-aksi">
          {can("finance_rekonsiliasi", "create") && (
            <button onClick={() => setShowUpload(true)} data-testid="bb-upload-csv"
              className="h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Upload className="w-4 h-4" /> Upload CSV
            </button>
          )}
          {can("finance_rekonsiliasi", "update") && (
            <button onClick={autoMatch} disabled={rekonBusy} data-testid="bb-automatch"
              className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
              <Zap className="w-4 h-4" /> {rekonBusy ? "Memproses..." : "Auto Match"}
            </button>
          )}
          <p className="text-[11px] text-slate-500">
            Mencocokkan nominal di buku besar dengan mutasi yang benar-benar masuk rekening.
          </p>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Akun</span>
          <select value={accountId} onChange={e => setAccountId(e.target.value)}
            data-testid="bb-akun"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[260px]">
            <option value="">Pilih akun...</option>
            {Object.entries(akunGrouped).map(([kategori, accs]) => (
              <optgroup key={kategori} label={CATEGORY_LABEL[kategori] || kategori}>
                {accs.map(a => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            data-testid="bb-dari"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            data-testid="bb-sampai"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
      </div>

      {!accountId && (
        <p className="text-sm text-slate-400 text-center py-12" data-testid="bb-empty-pilih">
          Pilih akun dulu untuk melihat buku besarnya.
        </p>
      )}

      {accountId && (
        <>
          {header && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi icon={Wallet} label={`Saldo Awal (${header.date_from || "awal"})`} value={idr(header.saldo_awal)} tint="slate" />
              <Kpi icon={TrendingUp} label="Total Debit Periode" value={idr(totalDebitPeriode)} tint="sky" />
              <Kpi icon={TrendingDown} label="Total Kredit Periode" value={idr(totalKreditPeriode)} tint="amber" />
              <Kpi icon={Scale} label="Saldo Akhir" value={idr(saldoAkhir)} tint="emerald" />
            </div>
          )}

          {terpotong && (
            <div
              data-testid="bb-terpotong-warning"
              className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800"
            >
              Daftar baris terpotong (maks 50.000) — persempit rentang tanggal; total &amp; saldo akhir tetap akurat.
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="bb-tabel">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display font-semibold text-slate-900">
                  {header ? `${header.code} — ${header.name}` : "Rincian Mutasi"}
                </h3>
                {header && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Saldo normal {header.arah_normal === "kredit" ? "kredit" : "debit"} · {totalRows} baris pada periode ini
                  </p>
                )}
              </div>
            </div>
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Tanggal</th>
                  <th className="text-left py-3 px-4">Deskripsi</th>
                  <th className="text-left py-3 px-4">Referensi</th>
                  <th className="text-right py-3 px-4">Debit</th>
                  <th className="text-right py-3 px-4">Kredit</th>
                  <th className="text-right py-3 px-4">Saldo Berjalan</th>
                  {bisaRekon && <th className="text-right py-3 px-4">Masuk Rekening</th>}
                  {bisaRekon && <th className="text-right py-3 px-4">Selisih</th>}
                  {bisaRekon && <th className="text-left py-3 px-4">SLA</th>}
                  {bisaRekon && <th className="text-center py-3 px-4">Selesai</th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={bisaRekon ? 10 : 6} className="text-center py-10 text-sm text-slate-400">Memuat...</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={bisaRekon ? 10 : 6} className="text-center py-10 text-sm text-slate-400" data-testid="bb-kosong">
                      Tidak ada mutasi pada rentang ini.
                    </td>
                  </tr>
                )}
                {!loading && items.map((it, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bb-baris-${i}`}>
                    <td className="py-2.5 px-4 text-slate-800 whitespace-nowrap">{it.tanggal}</td>
                    <td className="py-2.5 px-4 text-slate-700">
                      <div className="flex items-center gap-2">
                        {/* F1 — Deskripsi bisa diklik untuk membuka transaksi
                            asalnya. Baris tanpa nomor transaksi tetap teks
                            biasa: tautan yang tidak menuju ke mana pun lebih
                            buruk daripada tidak ada tautan (aturan 3
                            CLAUDE.md). */}
                        {it.ref ? (
                          <button
                            type="button"
                            onClick={() => telusuriSumber(it)}
                            disabled={telusurBusy === it.ref}
                            data-testid={`bb-deskripsi-telusur-${i}`}
                            title="Buka transaksi asal yang jadi sumber pencatatan ini"
                            className="truncate max-w-[280px] text-left text-sky-700 hover:text-sky-900 hover:underline disabled:opacity-50 disabled:cursor-wait"
                          >
                            {telusurBusy === it.ref ? "Membuka..." : (it.deskripsi || "-")}
                          </button>
                        ) : (
                          <span className="truncate max-w-[280px]">{it.deskripsi || "-"}</span>
                        )}
                        {it.is_reversal && (
                          <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-rose-50 text-rose-700 border-rose-200"
                            data-testid={`bb-reversal-${i}`}>
                            <RotateCcw className="w-3 h-3" /> reversal
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-4">
                      {(() => {
                        const src = _sourceOf(it);
                        return src ? (
                          <button
                            type="button"
                            onClick={() => setSourceModal(src)}
                            data-testid={`bb-ref-link-${i}`}
                            title="Buka dokumen sumber"
                            className="inline-flex items-center gap-1 font-mono text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline"
                          >
                            {it.ref || "-"} <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                          </button>
                        ) : (
                          <p className="font-mono text-[11px] text-slate-500">{it.ref || "-"}</p>
                        );
                      })()}
                      {it.source_module && <p className="text-[10px] text-slate-400">{it.source_module}</p>}
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono text-slate-700">{it.debit ? idr(it.debit) : "-"}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-slate-700">{it.credit ? idr(it.credit) : "-"}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-semibold text-slate-900">{idr(it.saldo_berjalan)}</td>
                    {bisaRekon && (
                      <>
                        <td className="py-2.5 px-4 text-right">
                          <input type="number" inputMode="numeric"
                            data-testid={`bb-masuk-${it.ref}`}
                            value={draft[it.ref]?.nominal_masuk ?? (it.nominal_masuk ?? "")}
                            onChange={e => setDraft(p => ({ ...p, [it.ref]: { ...p[it.ref], nominal_masuk: e.target.value } }))}
                            onBlur={() => draft[it.ref]?.nominal_masuk !== undefined && simpanRekon(it, {})}
                            placeholder="—"
                            className="w-28 h-8 px-2 text-right rounded border border-slate-200 text-xs font-mono" />
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-xs">
                          {it.selisih_rekon === null || it.selisih_rekon === undefined ? (
                            <span className="text-slate-300">—</span>
                          ) : Math.abs(it.selisih_rekon) < 0.5 ? (
                            <span className="text-emerald-700">cocok</span>
                          ) : (
                            <span className="text-rose-700 font-semibold" data-testid={`bb-selisih-${it.ref}`}>
                              {idr(it.selisih_rekon)}
                            </span>
                          )}
                          {/* Keterangan selisih hanya perlu diisi kalau memang ADA selisih —
                              memintanya untuk baris yang cocok cuma menambah pekerjaan. */}
                          {it.selisih_rekon !== null && it.selisih_rekon !== undefined
                            && Math.abs(it.selisih_rekon) >= 0.5 && (
                            <input
                              data-testid={`bb-ket-${it.ref}`}
                              value={draft[it.ref]?.keterangan_selisih ?? (it.keterangan_selisih || "")}
                              onChange={e => setDraft(p => ({ ...p, [it.ref]: { ...p[it.ref], keterangan_selisih: e.target.value } }))}
                              onBlur={() => draft[it.ref]?.keterangan_selisih !== undefined && simpanRekon(it, {})}
                              placeholder="alasan selisih"
                              className="mt-1 w-32 h-7 px-2 rounded border border-slate-200 text-[11px]" />
                          )}
                        </td>
                        <td className="py-2.5 px-4">
                          <input type="date"
                            data-testid={`bb-sla-${it.ref}`}
                            value={draft[it.ref]?.sla_tanggal ?? (it.sla_tanggal || "")}
                            onChange={e => setDraft(p => ({ ...p, [it.ref]: { ...p[it.ref], sla_tanggal: e.target.value } }))}
                            onBlur={() => draft[it.ref]?.sla_tanggal !== undefined && simpanRekon(it, {})}
                            className="h-8 px-2 rounded border border-slate-200 text-[11px]" />
                          <span className="block text-[10px] text-slate-400 mt-0.5">
                            {it.status_match === "matched" ? "Matched" : "Unmatched"}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-center">
                          {/* Syaratnya ditegakkan BACKEND — di sini kotaknya tetap bisa
                              diklik, dan kalau syaratnya kurang, pesan dari server
                              menyebutkan APA yang kurang. Menonaktifkannya di layar saja
                              akan membuat pengguna menebak-nebak kenapa. */}
                          <input type="checkbox" data-testid={`bb-selesai-${it.ref}`}
                            checked={!!it.rekon_selesai} disabled={rekonBusy}
                            onChange={e => simpanRekon(it, { selesai: e.target.checked })}
                            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && items.length < totalRows && (
              <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-center">
                <button onClick={muatLagi} disabled={loadingMore} data-testid="bb-muat-lagi"
                  className="h-10 px-5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium disabled:opacity-60">
                  {loadingMore ? "Memuat..." : `Muat Lagi (${items.length} dari ${totalRows})`}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {sourceModal && (
        <SourceDetailModal source={sourceModal} onClose={() => setSourceModal(null)} />
      )}
    </div>
  );
}

// Endpoint detail per tipe sumber — SUDAH ADA di masing-masing modul (bukan
// dibuat baru di sini): /sales/{id} (sales.py), /acquisitions/{id}
// (acquisitions.py), /payroll/slip/{id} (payroll.py, JSON — pasangan endpoint
// PDF yang sudah ada, RBAC sama persis: slip gaji data sensitif).
const SOURCE_ENDPOINT = {
  sale: (id) => `/sales/${id}`,
  acq: (id) => `/acquisitions/${id}`,
  payroll: (id) => `/payroll/slip/${id}`,
};
const SOURCE_TITLE = {
  sale: "Detail Penjualan",
  acq: "Detail Akuisisi",
  payroll: "Detail Slip Gaji",
};

function SourceDetailModal({ source, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let batal = false;
    setLoading(true); setError(null); setData(null);
    const url = SOURCE_ENDPOINT[source.type](source.id);
    api.get(url)
      .then(r => { if (!batal) setData(r.data); })
      .catch(e => { if (!batal) setError(e?.response?.data?.detail || "Gagal memuat dokumen sumber"); })
      .finally(() => { if (!batal) setLoading(false); });
    return () => { batal = true; };
  }, [source.type, source.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="bb-source-modal"
        className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">{SOURCE_TITLE[source.type]}</h3>
            <p className="text-[10px] font-mono text-slate-400 mt-0.5">{source.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {loading && <p className="text-sm text-slate-400 text-center py-10">Memuat...</p>}
        {!loading && error && (
          <p className="text-sm text-rose-600 text-center py-10" data-testid="bb-source-error">{error}</p>
        )}
        {!loading && !error && data && (
          <>
            {source.type === "sale" && <SaleDetail s={data} />}
            {source.type === "acq" && <AcqDetail a={data} />}
            {source.type === "payroll" && <PayrollDetail p={data} />}
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium text-right">{value ?? "-"}</span>
    </div>
  );
}

function SaleDetail({ s }) {
  const totalHarga = (s.items || []).reduce((sum, it) => sum + (Number(it.harga) || 0), 0);
  return (
    <div>
      <div className="space-y-0.5">
        <DetailRow label="Tanggal" value={s.tanggal} />
        <DetailRow label="Pelanggan" value={s.nama_pelanggan} />
        <DetailRow label="Penjual" value={s.nama_penjual} />
        <DetailRow label="Cabang" value={s.branch} />
        <DetailRow label="Metode Pembayaran" value={s.metode_pembayaran} />
      </div>
      {(s.items || []).length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Item</p>
          {s.items.map((it, i) => (
            <div key={i} className="flex justify-between items-center py-1 text-sm">
              <span className="text-slate-700 truncate max-w-[260px]">{it.nama_barang} {it.qty > 1 ? `×${it.qty}` : ""}</span>
              <span className="font-mono text-slate-900">{idr(it.harga)}</span>
            </div>
          ))}
          <div className="flex justify-between items-center pt-2 mt-1 border-t border-slate-200 font-semibold text-sm">
            <span>Total</span>
            <span className="font-mono">{idr(totalHarga)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AcqDetail({ a }) {
  return (
    <div className="space-y-0.5">
      <DetailRow label="Tanggal" value={a.tanggal} />
      <DetailRow label="Pelanggan" value={a.nama_pelanggan} />
      <DetailRow label="Tipe HP" value={a.type_hp} />
      <DetailRow label="IMEI" value={a.imei} />
      <DetailRow label="Harga Akuisisi" value={idr(a.harga_akuisisi)} />
      <DetailRow label="Cabang" value={a.branch} />
      <DetailRow label="Penerima" value={a.nama_penerima} />
      <DetailRow label="QC" value={a.qc_check} />
    </div>
  );
}

function PayrollDetail({ p }) {
  const c = p.calc || {};
  return (
    <div className="space-y-0.5">
      <DetailRow label="Karyawan" value={p.employee_nama} />
      <DetailRow label="NIK" value={p.employee_nik} />
      <DetailRow label="Grade" value={p.grade_jabatan} />
      <DetailRow label="Cabang" value={p.branch} />
      <DetailRow label="Periode" value={p.periode} />
      <div className="mt-3 border-t border-slate-100 pt-2 space-y-0.5">
        <DetailRow label="Total Bruto" value={idr(c.total_bruto)} />
        <DetailRow label="Total Potongan" value={idr(c.total_potongan)} />
        <div className="flex justify-between items-center pt-2 mt-1 border-t border-slate-200 font-bold text-sm text-emerald-700">
          <span>Take Home Pay</span>
          <span className="font-mono">{idr(c.thp)}</span>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-xl font-bold text-slate-900 mt-0.5 truncate">{value}</p>
    </div>
  );
}
